import {
  DEFAULT_CATEGORIES,
  DEFAULT_EXPENSE_CATEGORIES,
  DEFAULT_SHOP_SETTINGS,
  DEFAULT_UNITS,
  addUnit,
  addValue,
  fmtDateShort,
  purchaseLineTotal,
  purchaseTotal,
  rateOf,
  roundQty,
  todayKey,
  unitStep,
  type ActivityEntry,
  type CartLine,
  type CurrencyCode,
  type Customer,
  type DayKey,
  type JournalEntry,
  type NewCustomer,
  type PayMode,
  type Product,
  type PurchaseLine,
  type Sale,
  type ShopSettings,
  type UnitDef,
} from '@k2pos/core';
import { create } from 'zustand';
import * as api from '@/sync/api';
import type { ShopState } from '@/sync/api';

/**
 * The screens' view of the shop.
 *
 * **The server is the source of truth.** This store is a cache of what the
 * server last said, plus the few things that are genuinely local to one device
 * — the cart being rung up, which unit is selected, which customer it is for.
 * Every action posts to the server and replaces the cached half with the state
 * that comes back, so no arithmetic happens here that could drift from the
 * books, and the shop's other till sees the same numbers within seconds.
 *
 * What is deliberately *not* here any more: an outbox, a journal database, and
 * a persisted snapshot. A till that cannot reach the server cannot sell, and
 * says so — which is the trade the product made when it chose one set of books
 * over per-device ones.
 */

/** A payment taken against a customer's balance; drives the receive-done screen. */
export interface Receipt {
  customerId: string;
  customerName: string;
  amount: number;
  /** What is still owed after the payment. 0 closes the debt. */
  remaining: number;
}

/** One dated line in a customer's debt history. */
export interface HistoryEntry {
  id: string;
  date: string;
  detail: string;
  kind: 'credit' | 'paid';
  amount: number;
}

export interface NewProduct {
  name: string;
  cat: string;
  unit: string;
  /** Price per unit, in LAK. */
  price: number;
  /** Optional — only priced items appear in the report's profit strip. */
  cost?: number;
  /** Stock on hand at entry. Omitted means the product is not stock-tracked. */
  opening?: number;
  lowStockAt?: number;
  /** Set when the item came from the master catalogue; see `Product.masterId`. */
  masterId?: string;
  masterVersion?: number;
}

export interface NewExpense {
  category: string;
  /** LAK. */
  amount: number;
  note?: string;
  /** Paid from the till rather than by transfer. */
  cash: boolean;
  /** ISO-8601 with an offset. Omitted means now — an expense is often entered
   *  the next morning for the day before, so the screen lets it be chosen. */
  at?: string;
}

export interface NewPurchase {
  supplier: string;
  lines: PurchaseLine[];
  paid: boolean;
  cash: boolean;
  /** ISO-8601 with an offset. Goods often arrive before the paperwork. */
  at?: string;
}

interface PosState {
  // ---- from the server -----------------------------------------------------
  catalog: Product[];
  /** What the shop has stopped selling — the stock screen can show these. */
  retired: Product[];
  customers: Customer[];
  /** Newest first — the Home recent-activity list. */
  activity: ActivityEntry[];
  /** Every bill, newest first. */
  orders: Sale[];
  journal: JournalEntry[];

  salesToday: number;
  receivedToday: number;
  billsToday: number;
  collectedToday: number;

  shop: ShopSettings;
  units: UnitDef[];
  categories: string[];
  expenseCategories: string[];
  suppliers: string[];

  currentDay: DayKey;
  /**
   * The first day the ledger can answer for. Reports refuse to guess before it,
   * because a plausible-looking number for a day with no data is worse than an
   * honest gap.
   */
  journalStartedAt: DayKey;

  /** The server's state version. Sent back when polling to ask "anything new?". */
  version: number;
  /** True once the first state has arrived. Screens render empty until then. */
  loaded: boolean;

  // ---- local to this device ------------------------------------------------
  cart: CartLine[];
  /** Selected unit index per product id — a product can be sold by ຖົງ or ໂຕນ. */
  unitSel: Record<string, number>;
  /** Customer attached to the sale in progress; null means a walk-in cash buyer. */
  customerId: string | null;
  currency: CurrencyCode;
  /** The sale just completed, rendered by the done/invoice screen. */
  lastSale: Sale | null;
  /** The payment just taken, rendered by the receive-done screen. */
  lastReceipt: Receipt | null;

  // ---- selectors -----------------------------------------------------------
  selectedUnit: (productId: string) => number;
  qtyOf: (productId: string) => number;
  /** Every cart line for a product, across all its units. The row badge reads
   *  this rather than `qtyOf`, so switching unit can never hide a real line. */
  linesOf: (productId: string) => CartLine[];
  /** One customer's dated debt history, derived from the ledger. */
  historyOf: (customerId: string) => HistoryEntry[];

  // ---- cart, purely local --------------------------------------------------
  selectUnit: (productId: string, index: number) => void;
  bump: (productId: string, direction: 1 | -1) => void;
  setQty: (productId: string, qty: number) => void;
  clearCart: () => void;
  setCustomer: (customerId: string | null) => void;
  setCurrency: (currency: CurrencyCode) => void;

  // ---- the server ----------------------------------------------------------
  /** Replaces the cached half. Called by the poller and after every action. */
  apply: (state: ShopState) => void;
  /** Fetches the state. `poll` skips the work when nothing has changed. */
  refresh: () => Promise<void>;
  poll: () => Promise<void>;

  checkout: (payMode: PayMode) => Promise<Sale>;
  receivePayment: (customerId: string, amount: number) => Promise<Receipt>;
  addProduct: (input: NewProduct) => Promise<void>;
  updateProduct: (id: string, patch: api.ProductPatch) => Promise<void>;
  /** Retires a product, or brings one back. Never deletes. */
  retireProduct: (id: string, restore?: boolean) => Promise<void>;
  addCustomer: (input: NewCustomer) => Promise<Customer>;
  recordPurchase: (input: NewPurchase) => Promise<void>;
  addExpense: (input: NewExpense) => Promise<void>;
  reverseExpense: (id: string) => Promise<void>;
  /** "There are 37 on the shelf" — the server posts the difference. */
  countStock: (productId: string, counted: number) => Promise<void>;
  /** "Four broke" — the change is what is known, not the total. */
  adjustStock: (productId: string, delta: number, reason: string) => Promise<void>;
  closeDay: (counted: number | null) => Promise<void>;

  setRate: (code: Exclude<CurrencyCode, 'LAK'>, rate: number) => Promise<void>;
  updateShop: (patch: Partial<ShopSettings>) => Promise<void>;
  /** The shop's bank QR as a data URL. `null` removes it. */
  setShopQr: (image: string | null) => Promise<void>;
  /** One product's picture as a data URL. `null` removes it. */
  setProductImage: (id: string, image: string | null) => Promise<void>;
  addUnitOption: (unit: UnitDef) => Promise<void>;
  addCategory: (name: string) => Promise<void>;
  addExpenseCategory: (name: string) => Promise<void>;
  addSupplier: (name: string) => Promise<void>;
}

export function cartTotal(cart: CartLine[]): number {
  return cart.reduce((sum, line) => sum + line.price * line.qty, 0);
}

export const usePos = create<PosState>()((set, get) => ({
  // Empty until the first state arrives. Not seeded with mock data: a figure
  // that looks like the shop's own but is not would be worse than a blank.
  catalog: [],
  retired: [],
  customers: [],
  activity: [],
  orders: [],
  journal: [],

  salesToday: 0,
  receivedToday: 0,
  billsToday: 0,
  collectedToday: 0,

  shop: DEFAULT_SHOP_SETTINGS,
  units: DEFAULT_UNITS,
  categories: DEFAULT_CATEGORIES,
  expenseCategories: DEFAULT_EXPENSE_CATEGORIES,
  suppliers: [],

  currentDay: todayKey(),
  journalStartedAt: todayKey(),
  version: -1,
  loaded: false,

  cart: [],
  unitSel: {},
  customerId: null,
  currency: 'LAK',
  lastSale: null,
  lastReceipt: null,

  // ------------------------------------------------------------------------ //
  // Selectors
  // ------------------------------------------------------------------------ //

  selectedUnit: (productId) => get().unitSel[productId] ?? 0,

  qtyOf: (productId) => {
    const { cart, catalog } = get();
    const product = catalog.find((p) => p.id === productId);
    const unit = product?.units[get().selectedUnit(productId)]?.unit;
    return cart.find((l) => l.productId === productId && l.unit === unit)?.qty ?? 0;
  },

  linesOf: (productId) => get().cart.filter((l) => l.productId === productId),

  // `JournalEntry` is a discriminated union — a sale carries `total`, a payment
  // carries `amount` — so the two kinds are narrowed rather than read through
  // one field that only exists on some of them.
  historyOf: (customerId) =>
    get()
      .journal.flatMap<HistoryEntry>((e) => {
        if (e.kind === 'payment' && e.customerId === customerId) {
          return [
            {
              id: `h${e.id}`,
              date: fmtDateShort(new Date(e.at)),
              detail: 'ຮັບຊຳລະ',
              kind: 'paid',
              amount: e.amount,
            },
          ];
        }
        if (e.kind === 'sale' && e.payMode === 'credit' && e.customerId === customerId) {
          return [
            {
              id: `h${e.id}`,
              date: fmtDateShort(new Date(e.at)),
              detail: 'ຂາຍຕິດ',
              kind: 'credit',
              amount: e.total,
            },
          ];
        }
        return [];
      })
      .reverse(),

  // ------------------------------------------------------------------------ //
  // The cart — the one thing that is genuinely this device's
  // ------------------------------------------------------------------------ //

  selectUnit: (productId, index) =>
    set((s) => ({ unitSel: { ...s.unitSel, [productId]: index } })),

  bump: (productId, direction) => {
    const { catalog, cart } = get();
    const product = catalog.find((p) => p.id === productId);
    const unit = product?.units[get().selectedUnit(productId)];
    if (!product || !unit) return;

    const existing = cart.find((l) => l.productId === productId && l.unit === unit.unit);
    const next = roundQty((existing?.qty ?? 0) + direction * unit.step);
    get().setQty(productId, next);
  },

  setQty: (productId, qty) => {
    const { catalog, cart } = get();
    const product = catalog.find((p) => p.id === productId);
    const unit = product?.units[get().selectedUnit(productId)];
    if (!product || !unit) return;

    const rounded = roundQty(qty);
    const without = cart.filter((l) => !(l.productId === productId && l.unit === unit.unit));
    set({
      cart:
        rounded > 0
          ? [
              ...without,
              {
                productId,
                name: product.name,
                unit: unit.unit,
                price: unit.price,
                qty: rounded,
              },
            ]
          : without,
    });
  },

  clearCart: () => set({ cart: [], customerId: null }),
  setCustomer: (customerId) => set({ customerId }),
  setCurrency: (currency) => set({ currency }),

  // ------------------------------------------------------------------------ //
  // The server
  // ------------------------------------------------------------------------ //

  apply: (state) =>
    set({
      catalog: state.catalog,
      retired: state.retired ?? [],
      customers: state.customers,
      activity: state.activity,
      orders: state.orders,
      journal: state.journal,
      salesToday: state.salesToday,
      receivedToday: state.receivedToday,
      billsToday: state.billsToday,
      collectedToday: state.collectedToday,
      shop: state.shop,
      units: state.units,
      categories: state.categories,
      expenseCategories: state.expenseCategories,
      suppliers: state.suppliers,
      currentDay: state.currentDay,
      journalStartedAt: state.journalStartedAt,
      version: state.version,
      loaded: true,
      // A customer that no longer exists must not stay attached to the cart:
      // the sale would be refused at checkout with nothing on screen to explain
      // why.
      customerId:
        get().customerId && state.customers.some((c) => c.id === get().customerId)
          ? get().customerId
          : null,
    }),

  refresh: async () => {
    const state = await api.fetchState();
    if (state) get().apply(state);
  },

  poll: async () => {
    const { version, loaded } = get();
    const state = await api.fetchState(loaded ? version : undefined);
    // null means the server's version matched: nothing has changed, and there
    // is nothing to re-render.
    if (state) get().apply(state);
  },

  // ------------------------------------------------------------------------ //
  // Money
  // ------------------------------------------------------------------------ //

  checkout: async (payMode) => {
    const { cart, customerId, currency, shop } = get();
    const { state } = await api.sell({
      lines: cart.map((l) => ({
        productId: l.productId,
        name: l.name,
        unit: l.unit,
        price: l.price,
        qty: l.qty,
      })),
      payMode,
      currency,
      // Snapshotted onto the bill so a foreign-currency sale stays reconcilable
      // after the shop changes its rate.
      rate: rateOf(currency, shop.rates),
      ...(payMode === 'credit' && customerId ? { customerId } : {}),
    });

    get().apply(state);
    const sale = state.orders[0];
    if (!sale) throw new Error('the server accepted the sale but returned no bill');
    set({ lastSale: sale, cart: [], customerId: null });
    return sale;
  },

  receivePayment: async (customerId, amount) => {
    const before = get().customers.find((c) => c.id === customerId);
    const { state } = await api.receivePayment(customerId, amount);
    get().apply(state);

    const after = state.customers.find((c) => c.id === customerId);
    const receipt: Receipt = {
      customerId,
      customerName: after?.name ?? before?.name ?? '',
      // What the server actually took, which is clamped to what was owed —
      // never what was asked for.
      amount: (before?.balance ?? 0) - (after?.balance ?? 0),
      remaining: after?.balance ?? 0,
    };
    set({ lastReceipt: receipt });
    return receipt;
  },

  addProduct: async (input) => {
    const { state } = await api.addProduct(input);
    get().apply(state);
  },

  updateProduct: async (id, patch) => {
    const { state } = await api.updateProduct(id, patch);
    get().apply(state);
  },

  retireProduct: async (id, restore = false) => {
    const { state } = await api.retireProduct(id, restore);
    get().apply(state);
  },

  addCustomer: async (input) => {
    const { state, result } = await api.addCustomer(input);
    get().apply(state);
    const created = state.customers.find((c) => c.id === result);
    if (!created) throw new Error('the server accepted the customer but did not return them');
    return created;
  },

  recordPurchase: async (input) => {
    const { state } = await api.recordPurchase({
      supplier: input.supplier,
      lines: input.lines.map((l) => ({
        productId: l.productId,
        name: l.name,
        unit: l.unit,
        qty: l.qty,
        unitCost: l.unitCost,
      })),
      paid: input.paid,
      cash: input.cash,
      ...(input.at ? { at: input.at } : {}),
    });
    get().apply(state);
  },

  addExpense: async (input) => {
    const { state } = await api.addExpense(input);
    get().apply(state);
  },

  reverseExpense: async (id) => {
    const { state } = await api.reverseExpense(id);
    get().apply(state);
  },

  countStock: async (productId, counted) => {
    const { state } = await api.countStock(productId, counted);
    get().apply(state);
  },

  adjustStock: async (productId, delta, reason) => {
    const { state } = await api.adjustStock(productId, delta, reason);
    get().apply(state);
  },

  closeDay: async (counted) => {
    const { state } = await api.closeDay(counted);
    get().apply(state);
  },

  // ------------------------------------------------------------------------ //
  // Configuration
  // ------------------------------------------------------------------------ //

  setRate: async (code, rate) => {
    const { state } = await api.updateSettings({ rates: { ...get().shop.rates, [code]: rate } });
    get().apply(state);
  },

  updateShop: async (patch) => {
    const { state } = await api.updateSettings(patch);
    get().apply(state);
  },

  // The image is not in the state; `qrRev` in the answer is what tells every
  // screen showing a bill to fetch the new one. See `useShopQr`.
  setShopQr: async (image) => {
    const { state } = await api.setShopQr(image);
    get().apply(state);
  },

  // Same shape: the picture is not in the state, the product's `imageRev` in
  // the answer is what tells the sell screen to fetch it. See `useProductImages`.
  setProductImage: async (id, image) => {
    const { state } = await api.setProductImage(id, image);
    get().apply(state);
  },

  // Add-only, all four. Renaming or removing a value in use would orphan cart
  // lines, saved products and ledger entries — see `catalogue.ts`.
  addUnitOption: async (unit) => {
    if (get().units.some((u) => u.name === unit.name)) return;
    const { state } = await api.addVocabulary('unit', unit.name, unit.step);
    get().apply(state);
  },

  addCategory: async (name) => {
    const { state } = await api.addVocabulary('category', name);
    get().apply(state);
  },

  addExpenseCategory: async (name) => {
    const { state } = await api.addVocabulary('expense', name);
    get().apply(state);
  },

  addSupplier: async (name) => {
    const { state } = await api.addVocabulary('supplier', name);
    get().apply(state);
  },
}));

/** What the whole shop is owed. The hero figure on Home. */
export function totalDebt(customers: Customer[]): number {
  return customers.reduce((sum, c) => sum + c.balance, 0);
}

/** Re-exported so screens keep importing them from one place. */
export { addUnit, addValue, purchaseLineTotal, purchaseTotal, unitStep };
